import { ColorPicker } from '@/components/ColorPicker';
import { removeHexColorAlphaSuffix } from '@/components/ColorPicker/utils/removeHexColorAlphaSuffix';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { updateTemplate } from '@/services/api';
import { IProjectTemplate, TemplateVisibility, UpdateTemplateFormData } from '@d-id/types';
import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Autocomplete,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { StoreType } from 'polotno/model/store';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { validationSchema } from './validation-schema';
const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const GOOGLE_FONTS_ERROR_MESSAGE = 'Failed to load fonts. Please try again.';

const StyledTextField = styled(TextField)`
    & .MuiFormHelperText-root {
        font-size: 1.2rem; /* Adjust the font size as needed */
    }
`;

const ColorContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

interface Props {
    template: IProjectTemplate;
    templateData: StoreType | null;
    handleClose: () => void;
}

export const UpdateTemplateForm = ({ template, templateData, handleClose }: Props) => {
    const containerRef = useRef<HTMLFormElement | null>(null);
    const googleFontsQuery = useGoogleFonts();

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<UpdateTemplateFormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            templateName: template.name,
            tags: template.tags,
            fonts: {
                primary: templateData?.custom.fonts.primary || '',
                secondary: templateData?.custom.fonts.secondary,
                tertiary: templateData?.custom.fonts.tertiary,
            },
            colors: {
                primary: templateData?.custom.colors.primary || '',
                secondary: templateData?.custom.colors.secondary,
                tertiary: templateData?.custom.colors.tertiary,
            },
            visibility: template.visibility,
            rank: template.rank,
        },
    });

    const onSubmit = async (data: UpdateTemplateFormData) => {
        await updateTemplate(template.id, data);
        handleClose();
    };

    const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            return;
        }

        if (e.key === 'Tab') {
            e.preventDefault();
        }

        e.stopPropagation();
    };

    // prevent closing modal
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('keydown', handleTabKey);
        }

        return () => {
            if (container) {
                container.removeEventListener('keydown', handleTabKey);
            }
        };
    }, []);

    return (
        <StyledForm ref={containerRef} onSubmit={handleSubmit(onSubmit)}>
            <StyledTextField
                fullWidth
                label="Template Name"
                variant="outlined"
                inputProps={{ ...register('templateName') }}
                error={!!errors.templateName}
                helperText={errors.templateName ? errors.templateName.message : ''}
            />

            <Controller
                name="tags"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        onChange={(_, newValue) => {
                            onChange(newValue);
                        }}
                        onBlur={onBlur}
                        value={value}
                        renderTags={(value, getTagProps) => {
                            return value.map((option, index) => (
                                <Chip label={option} {...getTagProps({ index })} sx={{ fontSize: '1.5rem', margin: '4px' }} />
                            ));
                        }}
                        renderInput={params => <StyledTextField {...params} label="Tags" variant="outlined" />}
                    />
                )}
            />

            <Typography variant="h6">Fonts</Typography>
            <Controller
                name="fonts.primary"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        options={googleFontsQuery.data ?? []}
                        loading={googleFontsQuery.isLoading}
                        value={field.value}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        disabled={googleFontsQuery.isError}
                        renderInput={params => (
                            <StyledTextField
                                {...params}
                                label="Primary Font"
                                variant={googleFontsQuery.isError ? 'standard' : 'outlined'}
                                error={!!errors.fonts?.primary || googleFontsQuery.isError}
                                helperText={
                                    errors.fonts?.primary?.message ?? (googleFontsQuery.isError ? GOOGLE_FONTS_ERROR_MESSAGE : undefined)
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
            <Controller
                name="fonts.secondary"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        options={googleFontsQuery.data ?? []}
                        loading={googleFontsQuery.isLoading}
                        value={field.value}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        disabled={googleFontsQuery.isError}
                        renderInput={params => (
                            <StyledTextField
                                {...params}
                                label="Secondary Font"
                                variant={googleFontsQuery.isError ? 'standard' : 'outlined'}
                                error={!!errors.fonts?.secondary || googleFontsQuery.isError}
                                helperText={
                                    errors.fonts?.secondary?.message ?? (googleFontsQuery.isError ? GOOGLE_FONTS_ERROR_MESSAGE : undefined)
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
            <Controller
                name="fonts.tertiary"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        options={googleFontsQuery.data ?? []}
                        loading={googleFontsQuery.isLoading}
                        value={field.value}
                        onChange={(_, newValue) => field.onChange(newValue)}
                        disabled={googleFontsQuery.isError}
                        renderInput={params => (
                            <StyledTextField
                                {...params}
                                label="Tertiary Font"
                                variant={googleFontsQuery.isError ? 'standard' : 'outlined'}
                                error={!!errors.fonts?.tertiary || googleFontsQuery.isError}
                                helperText={
                                    errors.fonts?.tertiary?.message ?? (googleFontsQuery.isError ? GOOGLE_FONTS_ERROR_MESSAGE : undefined)
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {googleFontsQuery.isLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />

            <Typography variant="h6">Colors</Typography>
            <Controller
                name="colors.primary"
                control={control}
                render={({ field }) => (
                    <ColorContainer>
                        <ColorPicker size="48px" onChange={v => field.onChange(removeHexColorAlphaSuffix(v))} value={field.value || ''} />
                        <StyledTextField
                            {...field}
                            fullWidth
                            label="Primary Color"
                            variant="outlined"
                            placeholder="#FFFFFF"
                            error={!!errors.colors?.primary}
                            helperText={errors.colors?.primary ? errors.colors.primary.message : ''}
                        />
                    </ColorContainer>
                )}
            />

            <Controller
                name="colors.secondary"
                control={control}
                render={({ field }) => (
                    <ColorContainer>
                        <ColorPicker size="48px" onChange={v => field.onChange(removeHexColorAlphaSuffix(v))} value={field.value || ''} />
                        <StyledTextField {...field} fullWidth label="Secondary Color" variant="outlined" placeholder="#FFFFFF" />
                    </ColorContainer>
                )}
            />

            <Controller
                name="colors.tertiary"
                control={control}
                render={({ field }) => (
                    <ColorContainer>
                        <ColorPicker size="48px" onChange={v => field.onChange(removeHexColorAlphaSuffix(v))} value={field.value || ''} />
                        <StyledTextField {...field} fullWidth label="Tertiary Color" variant="outlined" placeholder="#FFFFFF" />
                    </ColorContainer>
                )}
            />

            <Typography variant="h6">Visibility</Typography>
            <FormControl component="fieldset">
                <Controller
                    name="visibility"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup row {...field}>
                            <FormControlLabel value={TemplateVisibility.SHOW} control={<Radio />} label="Show" />
                            <FormControlLabel value={TemplateVisibility.HIDE} control={<Radio />} label="Hide" />
                        </RadioGroup>
                    )}
                />
            </FormControl>

            <StyledTextField
                fullWidth
                label="Rank"
                variant="outlined"
                type="number"
                inputProps={{ ...register('rank') }}
                error={!!errors.rank}
                helperText={errors.rank ? errors.rank.message : ''}
            />

            <Button type="submit" variant="contained" color="primary">
                Save as Template
            </Button>
        </StyledForm>
    );
};
